import { ActionFunctionArgs, json, type MetaFunction } from '@remix-run/cloudflare';

import { Camp67Header } from '../components/camp67-header/camp67-header';

import { Footer } from '../components/footer';
import { sections, useInitializeSectionAnalytics } from '../hooks/use-section-analytics';
import { OneOfAKindSection } from '../sections/one-of-a-kind';
// import { LocationSection } from '../sections/location';
import { AccommodationAndAmenitiesSection } from '../sections/accommodation-and-amenities';
import { JoinUsSection } from '../sections/join-us';
import { useIsMobile } from '../hooks/use-is-mobile';
import { ApplicationEnvironment } from '../environment';
import { getOrigin } from '../get-origin';
import { retry } from '../retry';
import { propagateError } from '../propagate-error';
import React, { useCallback, useEffect, useState } from 'react';
import { AmplitudeEvent, analyticsState, ButtonTag } from '../state/amplitude';
import { getLowestPricedTentPrice } from '../state/get-tent-price';
import { WeLoveKumbhSection } from '../sections/we-love-kumbh';

import clsx from 'clsx';
import Sprite from '../sprites/sprite';
import { displayPrice } from '../state/prices';
import { useMessages } from '../i18n/useMessages';
import { useNavigate } from '@remix-run/react';
import {
  arePricesHidden,
  isInquiryModeEnabled,
  onOpenInquiry,
} from '../state/inquiry-modal';
import {
  StickyInquiryBar,
  MobileHeroCTA,
} from '../components/inquiry/sticky-inquiry-bar';
import { TentTypeV1 } from '@camp67/model';
import { TentExperienceModalV2 } from '../components/booking-form/tent-experience-modal-v2';

export const meta: MetaFunction = () => {
  return [
    { title: 'Magh Mela 2026 Accommodation Packages - camp67' },
    {
      name: 'description',
      content:
        'Register your interest for Magh Mela 2026 (Dec 31 - Feb 28). Safe, Secure, and Hygienic accommodation in Prayagraj.',
    },
    {
      tagName: 'link',
      name: 'canonical',
      href: 'https://camp67.com',
    },
  ];
};

export const action = async (args: ActionFunctionArgs) => {
  const request = args.request;
  const origin = getOrigin(request);
  const cookie = request.headers.get('cookie') ?? '';
  const environment = args.context.cloudflare.env as ApplicationEnvironment;

  const apiUrl = environment.API_URL;
  const formData = await request.formData();

  const email = formData.get('email')?.toString() ?? '';
  const intent = formData.get('intent')?.toString() ?? '';

  if (intent === 'subscribe') {
    try {
      const response = await retry('post-subscription', (signal) =>
        fetch(`${apiUrl}/api/subscribe`, {
          body: JSON.stringify({ email }),
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            origin,
            cookie,
          },
          signal,
        }),
      );

      if (!response.ok) {
        throw Error('Error: subscription failed');
      }

      return json({ success: true });
    } catch (e) {
      propagateError(e);
    }

    return json({ success: false });
  } else {
    const name = formData.get('name')?.toString() ?? '';
    const phone = formData.get('phone')?.toString() ?? '';
    const tentTypesRaw = formData.get('tentTypes')?.toString();
    const tentTypes = tentTypesRaw ? JSON.parse(tentTypesRaw) : undefined;
    const checkIn = formData.get('checkIn')?.toString() || undefined;
    const checkOut = formData.get('checkOut')?.toString() || undefined;
    const guestsRaw = formData.get('guests')?.toString();
    const guests = guestsRaw ? parseInt(guestsRaw, 10) : undefined;
    const message = formData.get('message')?.toString() || undefined;

    try {
      const response = await retry('post-inquiry', (signal) =>
        fetch(`${apiUrl}/api/inquiry`, {
          body: JSON.stringify({
            email,
            name,
            phone,
            tentTypes,
            checkIn,
            checkOut,
            guests,
            message,
          }),
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            origin,
            cookie,
          },
          signal,
        }),
      );

      if (!response.ok) {
        throw Error('Error: inquiry failed');
      }

      const data = (await response.json()) as { inquiryId: string };
      return json({ success: true, inquiryId: data.inquiryId });
    } catch (e) {
      propagateError(e);
    }

    return json({ success: false });
  }
};

export default function Index() {
  useInitializeSectionAnalytics();

  const getMessage = useMessages();

  const tagline = getMessage('camp67-header.tagline');
  const typewriterPrefix = getMessage('camp67-header.typewriter.prefix');
  const serene = getMessage('camp67-header.typewriter.serene');
  const typewriterSuffixStart = getMessage('camp67-header.typewriter.suffix.start');
  const typewriterSuffixEnd = getMessage('camp67-header.typewriter.suffix.end');
  const [tentModalType, setTentModalType] = useState<TentTypeV1 | null>(null);
  const onTentModalClose = useCallback(() => setTentModalType(null), []);

  return (
    <>
      {tentModalType ? (
        <TentExperienceModalV2 tentType={tentModalType} onClose={onTentModalClose} />
      ) : null}
      <FloatingBookButton />
      <section
        id={sections['header']}
        className="relative flex flex-col w-full min-h-[700px] xs:min-h-[750px] sm:min-h-[850px] md:h-[1080px] lg:h-[1140px] xl:h-[1400px] z-0 overflow-hidden max-h-dvh"
      >
        <Camp67Header isLogoLight={true} />
        {/* {headerCarousel.map((carouselItem, index) => (
          <img
            className={clsx(
              'camp67HeaderItem w-full min-h-[1080px] lg:min-h-[1400px] absolute top-0 left-0 -z-20 object-cover object-[40%_50%] xs:object-center -mt-8',
              {
                'opacity-100': index === activeImage,
                'opacity-0': index !== activeImage,
              },
            )}
            src={carouselItem.src}
            alt={carouselItem.alt}
            key={index}
            loading="lazy"
            id={`header-carousel-image-${index}`}
          />
        ))} */}
        <video
          className={clsx(
            'camp67HeaderItem w-full min-h-[1080px] lg:min-h-[1400px] absolute top-0 left-0 -z-20 object-cover object-[40%_50%] xs:object-center -mt-8',
          )}
          src="https://pub-3acbfdb36a6a4a758770a8294a62150d.r2.dev/camp67%20Hero%20Video.mp4"
          id={`header-carousel-image-0`}
          autoPlay={true}
          muted={true}
          loop={true}
          playsInline={true}
        />
        <div className="absolute left-1/2 -translate-x-1/2 top-[46%] sm:top-[48%] md:top-[50%] lg:top-[50%] -translate-y-1/2 w-full">
          {/* Event Badge */}
          <div className="flex justify-center mb-4 sm:mb-5 md:mb-6">
            <div className="inline-flex items-center gap-2 bg-black/30 backdrop-blur-sm border border-white/10 rounded-full px-3.5 py-1.5 sm:px-4 sm:py-2">
              <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-white/90 text-xs sm:text-sm font-medium tracking-wide">
                {getMessage('camp67-inquiry.event-badge')}
              </span>
            </div>
          </div>

          <hgroup className="text-center relative z-10 px-5 sm:px-4">
            <p className="font-serif uppercase text-[11px] sm:text-sm m-auto tracking-[0.18em] text-center md:text-sm xl:text-base text-amber-300 font-bold drop-shadow-lg">
              {tagline}
            </p>
            <h1 className="flex flex-col md:block text-[28px] leading-[36px] sm:text-[32px] sm:leading-[40px] font-serif font-medium text-white mt-3 sm:mt-4 md:text-5xl md:leading-[1.2] xl:text-6xl xl:leading-[1.1] drop-shadow-xl">
              {typewriterPrefix}{' '}
              <span>
                <span
                  id="header-typewriter"
                  className="text-amber-300 font-semibold drop-shadow-lg"
                >
                  {serene}
                </span>
                <br className="hidden md:block" /> {typewriterSuffixStart}{' '}
                <br className="block md:hidden" />
                {typewriterSuffixEnd}
              </span>
            </h1>
            <p className="mt-3 sm:mt-4 md:mt-5 text-white/80 text-sm md:text-base lg:text-lg max-w-xl mx-auto font-light hidden sm:block">
              {getMessage('camp67-inquiry.hero-subtitle')}
            </p>
          </hgroup>

          {/* CTA Section */}
          <div className="relative z-10 mt-6 sm:mt-7 md:mt-8 px-4">
            {isInquiryModeEnabled ? (
              <>
                <StickyInquiryBar />
                <MobileHeroCTA />
              </>
            ) : (
              <ReserveYourSpotButton
                openTentModal={() => {
                  setTentModalType(TentTypeV1.TWIN_ROOM);
                }}
              />
            )}
          </div>
        </div>
        {/* Enhanced overlays for better text readability and visual depth */}
        <div className="bg-gradient-to-b from-black/50 via-black/20 to-transparent w-full h-48 absolute top-0 -z-10" />
        <div className="bg-gradient-to-t from-black/40 via-black/20 to-transparent w-full h-64 absolute bottom-[6rem] -z-10" />

        {/* Subtle vignette effect */}
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.3)_100%)]" />
        <div className="w-full mt-auto h-28 md:h-26 lg:h-16 absolute bottom-[0px] bg-white">
          <img
            src="/bg_wave.svg"
            className="w-[105%] max-w-[unset] mt-auto absolute left-1/2 top-[1px] transform -translate-y-full -translate-x-1/2"
            alt=""
          />
        </div>
      </section>
      <main className="z-10 relative bg-white">
        <OneOfAKindSection />
        <AccommodationAndAmenitiesSection />
        <WeLoveKumbhSection />
        {/* <LocationSection /> */}
        <JoinUsSection />
      </main>
      <Footer />
    </>
  );
}

const ReserveYourSpotButton: React.FC<{ openTentModal: VoidFunction }> = ({
  openTentModal,
}) => {
  const getMessage = useMessages();

  const reserveSpotMobile = getMessage('camp67-header.reserve-your-spot.prefix.mobile');
  const reserveSpotDesktop = getMessage('camp67-header.reserve-your-spot.prefix.desktop');
  const secureCheckout = getMessage('camp67-header.reserve-your-spot.secure-checkout');

  const isMobile = useIsMobile();

  const navigate = useNavigate();

  const lowestPrice = displayPrice({
    priceInINR: getLowestPricedTentPrice(),
    showCurrency: true,
  });

  const isPricesExperimentEnabled = false;

  const onClick = useCallback(() => {
    if (isInquiryModeEnabled) {
      analyticsState?.amplitude?.track(AmplitudeEvent.BUTTON_CLICK, {
        sectionSource: sections['header'],
        buttonTag: ButtonTag.INQUIRE_NOW,
      });
      onOpenInquiry();
    } else {
      analyticsState?.amplitude?.track(AmplitudeEvent.BUTTON_CLICK, {
        sectionSource: sections['header'],
        buttonTag: ButtonTag.RESERVE_A_SPOT,
      });
      navigate('/packages/reservation');
    }
  }, [navigate]);

  const onClickPrices = useCallback(() => {
    analyticsState?.amplitude?.track(AmplitudeEvent.BUTTON_CLICK, {
      sectionSource: sections['header'],
      buttonTag: ButtonTag.SEE_PRICES,
    });
    openTentModal();
  }, [openTentModal]);

  return (
    <div className="flex flex-col sm:flex-row items-center gap-4 mt-8 justify-center m-auto">
      {isPricesExperimentEnabled ? (
        <button onClick={onClickPrices} className="max-w-[85%] relative">
          <div
            className={clsx(
              'w-full pt-5 pb-4 bg-blue-100 -skew-x-12 shadow-md-drop shadow-blue-200 rounded-lg hover:bg-blue-50 hover:shadow-gray-200 transition-all duration-300 ease-in-out',
              {
                'px-8 sm:px-8 lg:px-8': isInquiryModeEnabled,
                'px-12 sm:px-14 lg:px-16': !isInquiryModeEnabled,
              },
            )}
          >
            <p className="text-sm xl:text-base skew-x-12 uppercase tracking-widest hover:bold flex flex-row gap-0.5">
              {getMessage('camp67-header.see-prices')} →
            </p>
          </div>
        </button>
      ) : null}
      <button onClick={onClick} className="max-w-[85%] relative">
        <div
          className={clsx(
            'w-full pt-5 pb-4 bg-blue-100 -skew-x-12 shadow-md-drop shadow-blue-200 rounded-lg hover:bg-blue-50 hover:shadow-gray-200 transition-all duration-300 ease-in-out',
            {
              'px-8 sm:px-8 lg:px-8': isInquiryModeEnabled,
              'px-12 sm:px-14 lg:px-16': !isInquiryModeEnabled,
            },
          )}
        >
          <p className="text-sm xl:text-base skew-x-12 uppercase tracking-widest hover:bold flex flex-row gap-0.5">
            {isInquiryModeEnabled
              ? getMessage('camp67-header.inquiry')
              : isMobile
                ? reserveSpotMobile
                : reserveSpotDesktop}{' '}
            {!isInquiryModeEnabled && !arePricesHidden ? lowestPrice : ''} →
          </p>
          {!isInquiryModeEnabled ? (
            <div className="-mb-2 w-full pt-2 flex flex-row flex-wrap items-center justify-center">
              <div className="flex flex-row flex-wrap items-center justify-center">
                <Sprite
                  name="shield-dark"
                  width="14"
                  height="14"
                  className="skew-x-12 w-4 h-4"
                />
                <p className="skew-x-12 text-xs font-medium ml-1">{secureCheckout}</p>
              </div>
              <div className="flex flex-row items-center">
                <img
                  src={'/razorpay.png'}
                  alt="Razorpay"
                  className="h-3 sm:h-4 ml-2 object-contain skew-x-12"
                />
                <img
                  src={'/paypal.png'}
                  alt="Paypal"
                  className="h-4 sm:h-5 ml-2 object-contain skew-x-12"
                />
              </div>
            </div>
          ) : null}
        </div>
      </button>
    </div>
  );
};

// Floating Book Now Button Component
const FloatingBookButton: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const getMessage = useMessages();

  useEffect(() => {
    const handleScroll = () => {
      // Show button after user scrolls past the hero section (about 1000px for larger header)
      setIsVisible(window.scrollY > 1000);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const onClick = useCallback(() => {
    if (isInquiryModeEnabled) {
      analyticsState?.amplitude?.track(AmplitudeEvent.BUTTON_CLICK, {
        sectionSource: 'floating-cta',
        buttonTag: ButtonTag.INQUIRE_NOW,
      });
      onOpenInquiry();
    } else {
      analyticsState?.amplitude?.track(AmplitudeEvent.BUTTON_CLICK, {
        sectionSource: 'floating-cta',
        buttonTag: ButtonTag.RESERVE_A_SPOT,
      });
      // navigate('/packages/reservation');
    }
  }, []);

  const lowestPrice = displayPrice({
    priceInINR: getLowestPricedTentPrice(),
    showCurrency: true,
  });

  if (!isVisible) return null;

  return (
    <div className="hidden md:block fixed bottom-24 right-6 z-50 animate-in fade-in slide-in-from-right-8 duration-300">
      <button
        onClick={onClick}
        className="group bg-red-200 hover:bg-red-300 text-white px-6 py-4 rounded-full shadow-xl shadow-red-400/30 hover:shadow-2xl transform hover:scale-105 transition-all duration-300 ease-out border-2 border-white/20"
      >
        <div className="flex items-center gap-3">
          <div className="flex flex-col text-left">
            <span className="text-sm font-bold uppercase tracking-wide">
              {isInquiryModeEnabled
                ? getMessage('camp67-header.inquiry')
                : getMessage('camp67-header.reserve-your-spot.prefix.mobile')}
            </span>
            {!isInquiryModeEnabled && !arePricesHidden && (
              <span className="text-xs opacity-90">{lowestPrice}</span>
            )}
          </div>
          <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
            <svg
              className="w-3 h-3 transform group-hover:translate-x-0.5 transition-transform"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </div>
      </button>
    </div>
  );
};
